import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { Link, useParams } from 'react-router-dom';

import { Spinner } from '../../components/ui/Spinner.jsx';
import { useTranslation } from '../../i18n/useTranslation.js';
import { financeApi } from '../../services/financeApi.js';
import { salesApi } from '../../services/salesApi.js';
import { formatCurrency, formatDate, formatNumber } from '../../utils/formatters.js';
import { downloadInvoicePdf, previewInvoicePdf } from '../../utils/pdf.js';
import { InvoiceSendModal } from './InvoiceSendModal.jsx';
import { SwissQrBill } from './SwissQrBill.jsx';

/* Kein Rückfall auf einen fremden Absender.
 *
 * Hier standen fest verdrahtet Recicas Name und Recicas E-Mail. Solange es
 * eine Installation gab, war das ein Platzhalter. Mit mehreren Kundenfirmen
 * wird daraus ein Fehler mit Aussenwirkung: eine Firma, die ihre Angaben noch
 * nicht ausgefüllt hat, verschickt ihre Rechnung mit dem Briefkopf und der
 * Kontaktadresse eines fremden Unternehmens.
 *
 * Leer ist die ehrlichere Antwort. Wer den eigenen Namen auf der Rechnung
 * vermisst, trägt ihn in den Einstellungen nach. */

export function InvoicePrintPage() {
  const { t } = useTranslation();
  const { id } = useParams();
  const [emailOpen, setEmailOpen] = useState(false);

  const invoice = useQuery({
    queryKey: ['finance', 'invoices', id],
    queryFn: () => financeApi.getInvoice(id).then((r) => r.items),
  });
  const accounts = useQuery({
    queryKey: ['finance', 'accounts'],
    queryFn: () => financeApi.listAccounts().then((r) => r.items || []),
  });
  const settings = useQuery({
    queryKey: ['finance', 'company-settings'],
    queryFn: () => financeApi.getCompanySettings().then((r) => r.items),
  });

  const inv = invoice.data;
  const customer = useQuery({
    queryKey: ['sales', 'customer', inv?.customerId],
    queryFn: () => salesApi.getCustomer(inv.customerId).then((r) => r.items),
    enabled: Boolean(inv?.customerId),
  });

  if (invoice.isLoading) {
    return <section className="page"><Spinner /></section>;
  }
  if (invoice.error || !inv) {
    return (
      <section className="page">
        <div className="alert danger">{invoice.error?.message || 'Not found'}</div>
        <Link to="/finance/invoices" className="btn">← {t('finance.invoices.title')}</Link>
      </section>
    );
  }

  const accountById = new Map((accounts.data || []).map((a) => [a.id, a]));
  const party = customer.data;
  const cfg = settings.data || {};
  const outstanding = Math.max(0, Number(inv.totalAmount) - Number(inv.paidAmount));

  return (
    <div className="invoice-print-wrap">
      {/* Toolbar - hidden when printing */}
      <div className="invoice-toolbar no-print">
        <Link to={`/finance/invoices/${id}`} className="btn">← {t('common.back')}</Link>
        <div className="invoice-toolbar-actions">
          {/* Customers only get posted sales invoices - no drafts, no purchase docs. */}
          {inv.type === 'SALES' && inv.status !== 'DRAFT' && inv.status !== 'CANCELLED' && (
            <button type="button" className="btn" onClick={() => setEmailOpen(true)}>
              {t('email.send')}
            </button>
          )}
          <button type="button" className="btn" onClick={() => previewInvoicePdf({ invoice: inv, customer: party, settings: settings.data, t })}>
            {t('pdf.preview')}
          </button>
          <button
            type="button"
            className="btn"
            onClick={() => downloadInvoicePdf({ invoice: inv, customer: party, settings: settings.data, t })}
          >
            {t('invoice.print.downloadPdf')}
          </button>
          <button type="button" className="btn primary" onClick={() => window.print()}>
            {t('invoice.print.printOrSave')}
          </button>
        </div>
      </div>

      {/* The printable A4 document */}
      <article className="invoice-doc">
        <header className="invoice-doc-head">
          <div className="invoice-doc-brand">
            <div className="invoice-doc-brand-name">{cfg.companyName}</div>
            {cfg.addressLine1 && <div className="invoice-doc-muted">{cfg.addressLine1}</div>}
            {(cfg.zip || cfg.city) && <div className="invoice-doc-muted">{[cfg.zip, cfg.city].filter(Boolean).join(' ')}</div>}
            <div className="invoice-doc-muted">{cfg.email}</div>
            {cfg.iban && <div className="invoice-doc-muted">IBAN: {cfg.iban}</div>}
          </div>
          <div className="invoice-doc-title">
            <h1>{t('invoice.print.heading')}</h1>
            <div className="invoice-doc-number">{inv.invoiceNumber}</div>
          </div>
        </header>

        <section className="invoice-doc-parties">
          <div>
            <div className="invoice-doc-label">{t('invoice.print.billTo')}</div>
            <div className="invoice-doc-party-name">
              {party?.name || (inv.customerId || inv.vendorId || '').slice(0, 8)}
            </div>
            {party?.email && <div className="invoice-doc-muted">{party.email}</div>}
            {party?.code && <div className="invoice-doc-muted">{party.code}</div>}
          </div>
          <div className="invoice-doc-meta">
            <div><span>{t('col.date')}</span><strong>{formatDate(inv.invoiceDate)}</strong></div>
            <div><span>{t('col.due')}</span><strong>{formatDate(inv.dueDate)}</strong></div>
            <div><span>{t('col.status')}</span><strong>{t(`status.${inv.status}`)}</strong></div>
          </div>
        </section>

        <table className="invoice-doc-table">
          <thead>
            <tr>
              <th>{t('detail.lineDescription')}</th>
              <th className="num">{t('detail.lineQty')}</th>
              <th className="num">{t('detail.lineUnitPrice')}</th>
              <th className="num">{t('vat.rateCol')}</th>
              <th className="num">{t('detail.lineTotal')}</th>
            </tr>
          </thead>
          <tbody>
            {(inv.lines || []).map((r) => {
              const a = accountById.get(r.accountId);
              return (
                <tr key={r.id}>
                  <td>
                    <div>{r.description}</div>
                    {a && <div className="invoice-doc-muted">{a.code} - {a.name}</div>}
                  </td>
                  <td className="num">{formatNumber(r.quantity)}</td>
                  <td className="num">{formatCurrency(r.unitPrice, inv.currency)}</td>
                  <td className="num">{Number(r.taxRate || 0).toFixed(1)} %</td>
                  <td className="num">{formatCurrency(r.lineAmount, inv.currency)}</td>
                </tr>
              );
            })}
          </tbody>
        </table>

        <section className="invoice-doc-totals">
          <div className="invoice-doc-totals-box">
            <div><span>{t('vat.net')}</span><span>{formatCurrency(inv.netAmount ?? inv.totalAmount, inv.currency)}</span></div>
            <div><span>{t('vat.tax')}</span><span>{formatCurrency(inv.taxAmount ?? 0, inv.currency)}</span></div>
            <div><span>{t('invoice.print.total')}</span><strong>{formatCurrency(inv.totalAmount, inv.currency)}</strong></div>
            <div><span>{t('col.paid')}</span><span>{formatCurrency(inv.paidAmount, inv.currency)}</span></div>
            <div className="invoice-doc-due"><span>{t('detail.outstanding')}</span><strong>{formatCurrency(outstanding, inv.currency)}</strong></div>
          </div>
        </section>

        {inv.notes && (
          <section className="invoice-doc-notes">
            <div className="invoice-doc-label">{t('detail.notes')}</div>
            <p>{inv.notes}</p>
          </section>
        )}

        <footer className="invoice-doc-footer">
          {[cfg.companyName, cfg.email].filter(Boolean).join(' · ')}
        </footer>

        {/* Swiss QR-bill payment part */}
        <section className="invoice-doc-qr">
          <SwissQrBill
            settings={settings.data}
            invoice={inv}
            customer={party}
            currency={inv.currency}
          />
        </section>
      </article>

      <InvoiceSendModal invoice={emailOpen ? inv : null} onClose={() => setEmailOpen(false)} />
    </div>
  );
}
